// Native push registration (FCM via Capacitor). No-op on web/PWA - only runs
// inside the native (Capacitor) shell. Registers the device on a high-importance
// "rides" channel and sends the FCM token to the backend so the customer
// receives ride-status notifications (driver accepted/arrived, trip started/
// completed). The iOS AppDelegate (ios-firebase/) bridges the APNs token to the
// FCM registration token, so token.value is a valid FCM token on both platforms.
import { Capacitor } from '@capacitor/core';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

let registered = false;

export type PushState = 'unsupported' | 'denied' | 'pending' | 'registered' | 'error';

export function getPushState(): PushState {
  if (!Capacitor.isNativePlatform()) return 'unsupported';
  return (localStorage.getItem('pushStatus') as PushState) || 'pending';
}

// Live check of the ACTUAL OS notification permission (not the cached flag), so
// the enable-notifications prompt re-appears if a rider disables notifications in
// Settings. Returns true only in the native app with permission granted AND a
// token registered.
export async function isPushReady(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== 'granted') {
      localStorage.setItem('pushStatus', 'denied');
      return false;
    }
    // Permission is granted - that's all the prompt nags for. Re-asking can't
    // change an already-granted permission, so we stop showing it. If the token
    // hasn't finished registering (iOS APNs→FCM can lag, or a send failed),
    // retry quietly in the background instead of looping the prompt.
    if (getPushState() !== 'registered') refreshPushRegistration().catch(() => {});
    return true;
  } catch {
    return false;
  }
}

// Re-attempt registration (e.g. from an "Enable notifications" button).
export async function refreshPushRegistration(): Promise<void> {
  registered = false;
  await registerForPush();
}

export async function registerForPush(): Promise<void> {
  if (registered) return;
  try {
    if (!Capacitor.isNativePlatform()) return;
    // Dynamic import so the web bundle never tries to load the native plugin.
    const { PushNotifications } = await import('@capacitor/push-notifications');
    registered = true;

    // The channel id MUST match the backend's android.notification.channelId
    // ('rides'), or Android delivers on a low-importance default channel.
    try {
      await PushNotifications.createChannel({
        id: 'rides',
        name: 'Ride updates',
        description: 'Driver and trip status updates',
        importance: 5, // MAX - heads-up + sound + vibration
        visibility: 1,
        sound: 'default',
        vibration: true,
        lights: true,
      });
    } catch {}

    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') { localStorage.setItem('pushStatus', 'denied'); return; }

    await PushNotifications.register();

    // iOS without the push entitlement can hang silently - never leave the UI
    // stuck on "pending"; mark errored after 12s so a retry can show.
    setTimeout(() => {
      const s = localStorage.getItem('pushStatus');
      if (s !== 'registered' && s !== 'denied') localStorage.setItem('pushStatus', 'error');
    }, 12000);

    PushNotifications.addListener('registration', async (token) => {
      try {
        const jwt = localStorage.getItem('accessToken');
        const res = await fetch(`${API_URL}/notifications/device-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
          body: JSON.stringify({ token: token.value, platform: Capacitor.getPlatform() }),
        });
        localStorage.setItem('pushStatus', res.ok ? 'registered' : 'error');
        if (res.ok) localStorage.setItem('pushToken', token.value);
      } catch { localStorage.setItem('pushStatus', 'error'); }
    });

    PushNotifications.addListener('registrationError', () => { localStorage.setItem('pushStatus', 'error'); });

    // Foreground push: the OS won't show a banner while the app is open, so nudge
    // the active-ride poll to refresh status/driver position immediately. (No-op
    // if nothing is listening - the 3s ride poll already keeps the UI fresh.)
    const nudge = () => { try { window.dispatchEvent(new Event('sc-ride-push')); } catch {} };
    PushNotifications.addListener('pushNotificationReceived', nudge);
    PushNotifications.addListener('pushNotificationActionPerformed', nudge);

    // Clear the home-screen app-icon badge + notification tray whenever the app is
    // foregrounded. The OS badge only clears when the app's delivered notifications
    // are removed - opening the app (from the launcher) doesn't do that on its own,
    // so the "+1" lingered after reading. removeAllDeliveredNotifications also
    // resets the iOS icon badge.
    const clearDelivered = () => { PushNotifications.removeAllDeliveredNotifications().catch(() => {}); };
    document.addEventListener('visibilitychange', () => { if (!document.hidden) clearDelivered(); });
    clearDelivered();
  } catch {}
}
